/**
 * [T-068] query/mutation ของหน้ารายงาน — ต่อ backend T-065 (GET /api/v1/reports/*):
 * - ทุก query ใส่ from/to ตามวันไทย (Asia/Bangkok) เสมอ — แชร์ลิงก์แล้วเห็นเหมือนกัน
 * - /agents อาจ 403 (สิทธิ์/flag reports.per_agent) → ให้ UI ซ่อนแท็บเงียบ ๆ ไม่ใช่โยน error ใส่หน้า
 * - CSV: fetch blob + Content-Disposition → ดาวน์โหลดไฟล์จริง · 413 payload_too_large → บอกให้แคบช่วง
 * - schedules: CRUD ตาม ReportScheduleCreate/Patch — เวลาไทย (ICT=UTC+7) แปลงเป็น hour_utc ตอนส่ง
 */
import { queryOptions } from "@tanstack/react-query";
import { ErrorResponse, type ReportAgents, type ReportBacklog, type ReportCsat, type ReportGranularity, type ReportOverview, type ReportResponseTimes, type ReportSchedule, type ReportScheduleCreate, type ReportSchedulePatch, type ReportTimeInStatus, type ReportVolume } from "@uwu/contracts";
import { ApiError, api, isApiError, qs } from "./api";
import { currentLang } from "./i18n";

export const REPORT_KINDS = ["overview", "volume", "response-times", "time-in-status", "backlog", "csat", "agents"] as const;
export type ReportTabKind = (typeof REPORT_KINDS)[number];

export type ReportsSearch = {
  preset: "7d" | "30d" | "90d" | "custom";
  from: string;
  to: string;
  team_id?: string;
  service_id?: string;
  granularity: ReportGranularity;
};

/** วันที่ไทย (YYYY-MM-DD) ของ instant — วันเริ่ม 00:00 ไทยตาม W7 §5 */
export function thaiDateStr(d: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? "01";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** ช่วงของ preset — [from,to) ปลายเปิด รวมวันนี้ (to = เที่ยงคืนพรุ่งนี้ไทย) ให้ตรงค่าเริ่มต้นของ backend */
export function presetRange(preset: "7d" | "30d" | "90d", now: Date = new Date()): { from: string; to: string } {
  const days = preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
  const end = now.getTime() + 86_400_000;
  return { from: thaiDateStr(new Date(end - days * 86_400_000)), to: thaiDateStr(new Date(end)) };
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** search จาก URL → รูปร่างมาตรฐาน (ว่าง/เพี้ยน → 30d) — ใช้ทั้ง validateSearch ของ route และ layout */
export function normalizeReportsSearch(raw: Record<string, unknown>, now: Date = new Date()): ReportsSearch {
  const preset = raw.preset === "7d" || raw.preset === "90d" || raw.preset === "custom" ? raw.preset : "30d";
  const base = presetRange(preset === "custom" ? "30d" : preset, now);
  const from = typeof raw.from === "string" && DATE_RE.test(raw.from) ? raw.from : base.from;
  const to = typeof raw.to === "string" && DATE_RE.test(raw.to) ? raw.to : base.to;
  const granularity: ReportGranularity = raw.granularity === "week" || raw.granularity === "month" ? raw.granularity : "day";
  return {
    preset,
    from: from < to ? from : base.from,
    to: from < to ? to : base.to,
    team_id: typeof raw.team_id === "string" && raw.team_id ? raw.team_id : undefined,
    service_id: typeof raw.service_id === "string" && raw.service_id ? raw.service_id : undefined,
    granularity,
  };
}

/** query string ของรายงาน — ละ undefined ให้ backend ใช้ค่าเริ่มต้นตามสัญญา */
export function reportQs(s: ReportsSearch): string {
  return qs({ from: s.from, to: s.to, team_id: s.team_id, service_id: s.service_id, granularity: s.granularity });
}

export const reportsKeys = {
  report: (kind: ReportTabKind, s: ReportsSearch) => ["reports", kind, s.from, s.to, s.team_id ?? null, s.service_id ?? null, s.granularity] as const,
  schedules: ["reports", "schedules"] as const,
};

function reportPath(kind: ReportTabKind): string {
  return `/api/v1/reports/${kind}`;
}

/** query ต่อ kind — retry:false ให้ 403/404 ไปถึง UI เร็ว (ซ่อนแท็บ/แสดงสถานะอย่างสุภาพ) */
export function reportQuery<K extends ReportTabKind>(kind: K, s: ReportsSearch) {
  return queryOptions({
    queryKey: reportsKeys.report(kind, s),
    queryFn: () => api.get<ReportResponseOf<K>>(reportPath(kind) + reportQs(s)),
    retry: false,
    staleTime: 60_000,
  });
}

export type ReportResponseOf<K extends ReportTabKind> =
  K extends "overview" ? ReportOverview
  : K extends "volume" ? ReportVolume
  : K extends "response-times" ? ReportResponseTimes
  : K extends "time-in-status" ? ReportTimeInStatus
  : K extends "backlog" ? ReportBacklog
  : K extends "csat" ? ReportCsat
  : ReportAgents;

/** /agents ถูกปิด (สิทธิ์ไม่ถึง หรือ flag reports.per_agent ปิด) → API ตอบ 403/404 — แท็บหายเงียบ ๆ */
export function isAgentsDenied(e: unknown): boolean {
  return isApiError(e, 403) || isApiError(e, 404);
}

// ---------------------------------------------------------------------------
// CSV ดาวน์โหลด (R5) — blob + filename จาก Content-Disposition · 413 = payload_too_large
// ---------------------------------------------------------------------------

function filenameFromDisposition(disposition: string | null, kind: ReportTabKind): string {
  const m = disposition?.match(/filename="?([^";]+)"?/i);
  return m?.[1] ?? `report-${kind}.csv`;
}

/** ดาวน์โหลด CSV ของ kind ด้วยพารามิเตอร์เดียวกับหน้า — คืนชื่อไฟล์ที่ได้ */
export async function downloadReportCsv(kind: ReportTabKind, s: ReportsSearch): Promise<string> {
  const res = await fetch(reportPath(kind) + ".csv" + reportQs(s), {
    credentials: "include",
    headers: { "X-Requested-With": "uwu-web", "Accept-Language": currentLang() },
  });
  if (!res.ok) {
    const text = await res.text();
    let data: unknown = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    const parsed = ErrorResponse.safeParse(data);
    if (parsed.success) {
      throw new ApiError(res.status, parsed.data.error.code, parsed.data.error.message, parsed.data.error.details ?? [], parsed.data.error.request_id);
    }
    throw new ApiError(res.status, "http_" + res.status, res.statusText || "Request failed");
  }
  const filename = filenameFromDisposition(res.headers.get("content-disposition"), kind);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return filename;
}

// ---------------------------------------------------------------------------
// schedules (R6)
// ---------------------------------------------------------------------------

export const schedulesQuery = queryOptions({
  queryKey: reportsKeys.schedules,
  queryFn: () => api.get<{ items: ReportSchedule[] }>("/api/v1/reports/schedules"),
  retry: false,
});

export function createSchedule(body: ReportScheduleCreate): Promise<ReportSchedule> {
  return api.post<ReportSchedule>("/api/v1/reports/schedules", body);
}

export function patchSchedule(id: string, body: ReportSchedulePatch): Promise<ReportSchedule> {
  return api.patch<ReportSchedule>(`/api/v1/reports/schedules/${encodeURIComponent(id)}`, body);
}

export function deleteSchedule(id: string): Promise<void> {
  return api.del<void>(`/api/v1/reports/schedules/${encodeURIComponent(id)}`);
}

/** เวลาไทย (ICT = UTC+7 คงที่) → ชั่วโมง UTC ที่ส่งให้ API */
export function thaiHourToUtc(hourThai: number): number {
  return ((Math.round(hourThai) - 7) % 24 + 24) % 24;
}

/** ชั่วโมง UTC ที่ API ส่งมา → เวลาไทยสำหรับแสดงผล */
export function utcHourToThai(hourUtc: number): number {
  return (Math.round(hourUtc) + 7) % 24;
}

/** ผู้รับแนะนำจาก GET /api/v1/users?q= (T-032 R4 — ทุก staff ใช้ได้ · กรอง kind=staff ที่ UI) */
export type UserSuggest = { id: string; display_name: string | null; avatar_url: string | null; kind: string; primary_email: string | null };

export const userSuggestQuery = (q: string) =>
  queryOptions({
    queryKey: ["reports", "user-suggest", q] as const,
    queryFn: () => api.get<{ items: UserSuggest[] }>(`/api/v1/users${qs({ q, limit: 10 })}`),
    enabled: q.trim().length > 0,
    retry: false,
    staleTime: 30_000,
  });
